// In-chat "Your Symphony card" — pure helpers shared by the chat state (use-chat via `readChatCards`), the BFF
// history mapping (messages API) and the card UI (MessageRow/ProfileLinkChatCard). The wire shape is the harness
// contract: the FINAL assistant reply of a turn whose ManageProfileLinks action left a link the owner should look
// at (claim, create, update, publish, pause, resume, archive) carries `metadata.profileLinkCard`, and the SSE `messageEnd` event carries
// the same card in-band, like `connectorCard`.
//
// Named `profile-link-CHAT-card` because `lib/profile-link-card.ts` is already the PUBLIC face of a link (what
// `/me/<handle>` renders) — a different shape for a different reader.
//
// Deliberately dependency-free (mirrors lib/connector-card.ts): unit tests must not drag the common-lib graph,
// so the validation here MIRRORS common-lib's `readProfileLinkCard` — fail closed on ANY malformed field, https
// only for `url` — rather than importing it. Keep the two in step, and the iOS/Android twins with them.

// ARCHIVED is stamped by the archive action so the latest-card-wins rule can retire the earlier cards.
export const PROFILE_LINK_CARD_STATUSES = ['DRAFT', 'ACTIVE', 'PAUSED', 'ARCHIVED'] as const;
export type ProfileLinkCardStatus = (typeof PROFILE_LINK_CARD_STATUSES)[number];

export type ProfileLinkCardData = {
  /** The link's id — what Edit acts on, and what the latest-card-wins rule groups by. */
  linkId: string;
  /** The public page, https, on whatever host the harness wrote (`symphony.wix.app` today) — what Open and Share hand out. */
  url: string;
  handle: string;
  /** The occasion's label, on a context link only. */
  contextTitle?: string;
  /** The card headline as of this turn (the draft's, on a draft). */
  headline?: string;
  /** The link's state right after the turn's write — the live state comes from the profile-links list. */
  status: ProfileLinkCardStatus;
  /** Still on the wire; no surface reads it (a saved change is a live change). */
  hasUnpublishedChanges?: boolean;
  mintedAt: string;
};

/** A link id as the owner API route accepts one (`lib/profile-links-route.ts` `ID_RE`); anything else is not forwarded. */
const LINK_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const isText = (value: unknown): value is string => typeof value === 'string' && value.trim().length > 0;

/**
 * Read + validate `metadata.profileLinkCard` off a raw message entity (or the `messageEnd` payload). Fails CLOSED:
 * any required field missing or malformed, or any optional field present with the wrong type, and the reply
 * renders its words only — never a broken card, and never a non-https URL behind the Open button.
 */
export function readProfileLinkCard(metadata: unknown): ProfileLinkCardData | undefined {
  const raw = (metadata as { profileLinkCard?: unknown } | null | undefined)?.profileLinkCard;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return undefined;
  const { linkId, url, handle, contextTitle, headline, status, hasUnpublishedChanges, mintedAt } = raw as Record<string, unknown>;
  if (
    typeof linkId !== 'string' ||
    !LINK_ID_RE.test(linkId) ||
    typeof url !== 'string' ||
    !/^https:\/\/\S+$/i.test(url) ||
    !isText(handle) ||
    typeof status !== 'string' ||
    !(PROFILE_LINK_CARD_STATUSES as readonly string[]).includes(status) ||
    !isText(mintedAt) ||
    (contextTitle !== undefined && typeof contextTitle !== 'string') ||
    (headline !== undefined && typeof headline !== 'string') ||
    (hasUnpublishedChanges !== undefined && typeof hasUnpublishedChanges !== 'boolean')
  ) {
    return undefined;
  }
  return {
    linkId,
    url,
    handle,
    status: status as ProfileLinkCardStatus,
    mintedAt,
    ...(isText(contextTitle) ? { contextTitle: contextTitle.trim() } : {}),
    ...(isText(headline) ? { headline: headline.trim() } : {}),
    ...(hasUnpublishedChanges === true ? { hasUnpublishedChanges: true as const } : {}),
  };
}

/**
 * What the card draws. There is no publish step anywhere a user sees: the agent's write leaves the link live.
 * - `draft` — not live yet (a legacy never-live link, or a go-live the service refused): Not live yet + Edit.
 * - `live` — Live badge, Open + Share.
 * - `paused` — Paused badge + Edit.
 * - `gone` — archived: the card draws nothing (its words still say what happened).
 */
export type ProfileLinkCardState = 'draft' | 'live' | 'paused' | 'gone';

/**
 * The card's state. An ARCHIVED card (the latest card for the link, see `latestProfileLinkCards`) is gone whatever
 * else is known. Otherwise the owner's own profile-links list WINS when it is loaded and holds the link — a card's
 * `status` is only what was true when its turn minted it; failing that, the card's status. `hasUnpublishedChanges`
 * is not read: a saved change is a live change.
 */
export function deriveProfileLinkCardState(input: {
  card: Pick<ProfileLinkCardData, 'status'>;
  live?: { status: string } | null;
}): ProfileLinkCardState {
  const fromStatus = (status: string): ProfileLinkCardState => {
    if (status === 'ACTIVE') return 'live';
    if (status === 'PAUSED') return 'paused';
    if (status === 'DRAFT') return 'draft';
    return 'gone';
  };
  if (input.card.status === 'ARCHIVED') return 'gone';
  if (input.live) return fromStatus(input.live.status);
  return fromStatus(input.card.status);
}

/**
 * Latest card wins: the harness stamps a card after EVERY write (create, update, publish, pause, resume, archive)
 * with the status after it, so within a loaded conversation the LAST card for a link (message order) is the truest
 * one, and every earlier card for that link draws it instead of its own stale minting. Without this, "archive it"
 * left the card above saying Live with an Open onto a not-found page. Same rule on iOS and Android.
 */
export function latestProfileLinkCards(messages: ReadonlyArray<{ profileLinkCard?: ProfileLinkCardData }>): Map<string, ProfileLinkCardData> {
  const latest = new Map<string, ProfileLinkCardData>();
  for (const message of messages) {
    const card = message.profileLinkCard;
    if (card) latest.set(card.linkId, card);
  }
  return latest;
}

/**
 * When the latest card and the loaded profile-links list disagree about a link — the list was read BEFORE the
 * agent's write (card PAUSED vs list ACTIVE, card ACTIVE vs list DRAFT, or a link the list has never seen) — the
 * list is stale and should be read again. Returns the key of that disagreement, or `null` when there is nothing to
 * re-read (no list yet, an archived card, or they agree). The caller re-reads ONCE per key, so a list that still
 * disagrees after the re-read does not loop. Same rule on iOS and Android.
 */
export function profileLinkListRefreshKey(
  card: Pick<ProfileLinkCardData, 'linkId' | 'status' | 'mintedAt'>,
  links: ReadonlyArray<{ id: string; status: string }> | undefined,
): string | null {
  if (!links || card.status === 'ARCHIVED') return null;
  const row = links.find((link) => link.id === card.linkId);
  if (row && row.status === card.status) return null;
  return `${card.linkId}:${card.status}:${card.mintedAt}`;
}

/** `https://symphony.wix.app/me/ronny/` → `symphony.wix.app/me/ronny` — the address line under the title, any host. */
export function profileLinkCardDisplayUrl(url: string): string {
  return url.replace(/^https:\/\//i, '').replace(/\/+$/, '');
}

/** Where Edit goes: that link's screen with its editor open on the Card section. */
export function profileLinkEditorRoute(linkId: string): string {
  return `/profile-links/${encodeURIComponent(linkId)}?tab=card`;
}
